/**
 * High-intent English keywords for crypto payment gateway SEO.
 * Based on common Google queries (gateway comparisons, USDT checkout, Cryptopay-style terms).
 * Meta keywords have limited direct ranking weight but help relevance; titles/descriptions/JSON-LD matter more.
 */

import { getBrand } from './brand.config';

/** Dedupe and join keyword groups (comma-separated meta keywords). */
export function mergeKeywords(...groups: string[][]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const group of groups) {
    for (const raw of group) {
      const k = raw.trim();
      if (!k) continue;
      const key = k.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(k);
    }
  }
  return out.join(', ');
}

function brandKeywords(): string[] {
  const b = getBrand();
  return [
    b.name,
    b.fullName,
    b.domain,
    'www.' + b.domain,
    b.apiUrl.replace(/^https?:\/\//, ''),
    'api.' + b.domain,
  ];
}

/** Legacy / alternate spellings users still search */
const LEGACY_BRAND = [
  'Crypopay',
  'Crypopay.com',
  'crypopay',
  'crypopay payment gateway',
];

/** Cryptopay-style queries (high search intent; distinct from tropayment brand) */
const CRYPTOPAY_VARIANTS = [
  'cryptopay',
  'CryptoPay',
  'crypto pay',
  'cryptopay payment gateway',
  'cryptopay merchant',
  'cryptopay checkout',
  'cryptopay api',
];

/** Core payment gateway (most searched category terms) */
const GATEWAY_CORE = [
  'crypto payment gateway',
  'cryptocurrency payment gateway',
  'crypto payment processor',
  'blockchain payment gateway',
  'digital currency payment gateway',
  'cryptocoin payment gateway',
  'merchant crypto payment',
  'accept crypto payments',
  'accept cryptocurrency payments',
  'accept bitcoin payments',
  'crypto checkout',
  'online crypto payment',
  'crypto payment solution',
  'crypto payment platform',
  'payment gateway for cryptocurrency',
  'low fee crypto payment gateway',
  'best crypto payment gateway',
];

/** Stablecoins & checkout */
const STABLECOIN = [
  'USDT payment gateway',
  'USDC payment gateway',
  'stablecoin payment gateway',
  'pay with USDT',
  'pay with USDC',
  'accept USDT payments',
  'USDT checkout',
  'TRC20 USDT payment',
  'ERC20 USDT payment',
  'BEP20 USDT payment',
  'USDT merchant gateway',
  'crypto stablecoin payments',
];

/** Major coins */
const COINS = [
  'bitcoin payment gateway',
  'ethereum payment gateway',
  'BTC payment gateway',
  'ETH payment',
  'TRX payment',
  'BNB payment',
  'SOL payment',
  'MATIC payment',
  'pay with bitcoin',
  'pay with ethereum',
  'pay with crypto',
];

/** Networks / chains */
const NETWORKS = [
  'TRON payment gateway',
  'TRC20 payment gateway',
  'ERC20 payment gateway',
  'BSC payment gateway',
  'BEP20 payment',
  'Polygon payment gateway',
  'Solana payment gateway',
  'multi-chain crypto payment',
  'cross-chain payment gateway',
];

/** API & developers */
const API_DEV = [
  'crypto payment API',
  'cryptocurrency payment API',
  'payment gateway API',
  'merchant payment API',
  'REST API crypto payments',
  'webhook payment gateway',
  'HMAC payment API',
  'integrate crypto payments',
  'crypto payment integration',
];

/** E-commerce */
const ECOMMERCE = [
  'WooCommerce crypto payment',
  'WordPress crypto payment gateway',
  'Shopify crypto payment',
  'e-commerce crypto payments',
  'invoice crypto payment',
  'payment link crypto',
];

export const SEO_KEYWORD_GROUPS = {
  brand: brandKeywords,
  legacy: () => LEGACY_BRAND,
  cryptopay: () => CRYPTOPAY_VARIANTS,
  gateway: () => GATEWAY_CORE,
  stablecoin: () => STABLECOIN,
  coins: () => COINS,
  networks: () => NETWORKS,
  api: () => API_DEV,
  ecommerce: () => ECOMMERCE,
} as const;

export type SeoKeywordGroup = keyof typeof SEO_KEYWORD_GROUPS;

export function keywordsFor(...groups: SeoKeywordGroup[]): string {
  return mergeKeywords(...groups.map((g) => SEO_KEYWORD_GROUPS[g]()));
}

/** Homepage + default fallback — broad coverage */
export const GLOBAL_KEYWORDS = keywordsFor(
  'brand',
  'legacy',
  'cryptopay',
  'gateway',
  'stablecoin',
  'coins',
  'networks',
  'api',
  'ecommerce'
);

/** JSON-LD alternateName — from brand config + gateway terms */
export function getAlternateBrandNames(): string[] {
  const b = getBrand();
  return mergeKeywords(
    [...b.seoAlternateNames],
    LEGACY_BRAND,
    CRYPTOPAY_VARIANTS,
    ['Crypto Payment Gateway', 'Cryptocurrency Payment Gateway']
  )
    .split(', ')
    .filter(Boolean);
}

export function getDefaultMetaDescription(): string {
  const b = getBrand();
  return (
    `${b.fullName} is a crypto payment gateway for merchants. ` +
    `Accept USDT, USDC, and native coins on TRON, Ethereum, BSC, Polygon, and Solana. ` +
    `Secure merchant API at ${b.apiUrl.replace(/^https?:\/\//, '')}, webhooks, hosted checkout.`
  );
}
